import { Box } from '@mui/material'
import {
  GRID_TREE_DATA_GROUPING_FIELD,
  GridCellParams,
  GridColumnGroupingModel,
  GridRowClassNameParams,
} from '@mui/x-data-grid-premium'
import { UnfoldLessOutlined, UnfoldMoreOutlined } from '@mui/icons-material'
import { useCallback, useMemo } from 'react'
import { toast } from 'react-hot-toast'
import { Button } from 'Common/Supplyvue-ui'
import { BaseTable } from 'Features/Projects/Table/BaseTable.view'
import { ExtendedGridColDef } from 'Features/Projects/Table/BulkUpdateModal'
import { useSettingsContext } from 'Features/Projects/Scenarios/EditSettings/EditSettings.types'
import { useLinePreview, useSaveLineSettings } from '../lineRecalculation'
import {
  LinePreviewNote,
  toastLineSaved,
  toastLineSaveFailed,
} from '../LinePreviewNote'
import {
  cycleOverrideColumn,
  setByColumn,
  showProblems,
  useCycleGrid,
} from '../cycleColumns'
import { TOTAL_SHAPE, TotalRow, totalEdits } from './totalRows'

const OVERRIDES = ['minOverride', 'maxOverride']
// Shown, and editable, on SKU group rows only.
const SKU_ONLY = new Set([
  'skuGroupName',
  'workCentreAllocation',
  'batchMin',
  'runRate',
])

const number = (digits: number) => (value: number | null) =>
  value === null || value === undefined ? '' : value.toFixed(digits)

const COLUMN_GROUPS: GridColumnGroupingModel = [
  {
    groupId: 'Demand',
    children: [
      { field: 'salesRate' },
      { field: 'workCentreAllocation' },
      { field: 'minOrder' },
    ],
  },
  {
    groupId: 'From the rules',
    description: 'What the minimum order and the batch give, before overrides',
    children: [
      { field: 'naturalCycle' },
      { field: 'initialCycle' },
      { field: 'batchMin' },
    ],
  },
  {
    groupId: 'Overrides',
    description: 'Type 1, 2, 4 or 8 on a cell; Delete clears it',
    children: [{ field: 'minOverride' }, { field: 'maxOverride' }],
  },
  {
    groupId: 'Result',
    description: 'What the template generator uses',
    children: [
      { field: 'minCycle' },
      { field: 'maxCycle' },
      { field: 'cycleSetBy' },
      { field: 'replenishmentTypeCode' },
      { field: 'isIncluded' },
    ],
  },
]

const INITIAL_STATE = {
  pagination: { paginationModel: { pageSize: 100 } },
  columns: { columnVisibilityModel: { unitOfMeasureCode: false } },
}

const GROUPING_COL_DEF = {
  headerName: 'Batch / SKU group',
  width: 220,
  // The row's own label, not its tree key (which also holds the line).
  valueFormatter: (_value: unknown, row: TotalRow) => row?.label,
}

const isBatch = (row: { rowKind?: unknown }) => row?.rowKind === 'batches'

/**
 * Total production: each batch group with its SKU groups under it, both
 * editable in one grid. A batch edit previews on its SKU groups straight
 * away (and the reverse: a SKU group's demand moves its batch), through the
 * same cycle rules as SKU production and Batch production.
 */
export const TotalProductionTable = ({
  data,
  isLoading,
}: {
  data: TotalRow[]
  isLoading: boolean
}) => {
  const { state } = useSettingsContext()
  const { projectId, scenarioId } = state
  const preview = useLinePreview({
    kind: 'products',
    projectId,
    scenarioId,
    data,
    toEdits: totalEdits,
    shape: TOTAL_SHAPE,
  })
  const cycleGrid = useCycleGrid({
    kind: 'total',
    data,
    overrideFields: OVERRIDES,
    previewApiRef: preview.apiRefCallback,
    problemsAt: preview.problemsAt,
  })
  const { mutate: save, isPending: isSaving } = useSaveLineSettings(
    projectId,
    scenarioId,
    toastLineSaved,
    (error) => {
      preview.saveFailed(error)
      toastLineSaveFailed(error)
    }
  )

  const problems = cycleGrid.problems
  const columns: ExtendedGridColDef[] = useMemo(
    () => [
      {
        field: 'skuGroupName',
        headerName: 'SKU group name',
        width: 180,
        type: 'string',
      },
      {
        field: 'baseSourceSiteCode',
        headerName: 'Site',
        width: 88,
        type: 'string',
      },
      {
        field: 'workCentreCode',
        headerName: 'Work centre',
        width: 112,
        type: 'string',
      },
      {
        field: 'unitOfMeasureCode',
        headerName: 'Unit of measure',
        width: 120,
        type: 'string',
      },
      {
        field: 'salesRate',
        headerName: 'Sales rate',
        description:
          "Weekly demand on this line. A batch: its SKU groups' allocated sales in the batch unit. A SKU group: its allocated sales.",
        width: 104,
        type: 'number',
        valueFormatter: number(2),
      },
      {
        field: 'workCentreAllocation',
        headerName: 'Allocation',
        description: 'Share of the demand made on this line (Allocations tab)',
        width: 96,
        type: 'number',
        valueFormatter: (value: number | null) =>
          value === null || value === undefined
            ? ''
            : `${Number((value * 100).toFixed(2))}%`,
      },
      showProblems(
        {
          field: 'minOrder',
          headerName: 'Min order',
          description:
            'A batch: its minimum batch size. A SKU group: its MOQ, in product units.',
          width: 104,
          type: 'number',
          editable: true,
        },
        problems
      ),
      {
        field: 'naturalCycle',
        headerName: 'Natural',
        description: 'Weeks one minimum order lasts at the sales rate',
        width: 88,
        type: 'number',
        valueFormatter: number(2),
      },
      {
        field: 'initialCycle',
        headerName: 'Initial',
        description: 'The natural cycle rounded up to 1, 2, 4 or 8',
        width: 80,
        type: 'number',
      },
      {
        field: 'batchMin',
        headerName: 'Batch min',
        description:
          "A SKU group's batch's min cycle: it can't run more often than its batch",
        width: 96,
        type: 'number',
      },
      cycleOverrideColumn({
        field: 'minOverride',
        headerName: 'Min override',
        description:
          'Run no more often than this. Type 1, 2, 4 or 8; Delete clears it.',
        bound: 'min',
        minInput: 'minCycle',
        maxInput: 'maxCycle',
        problems,
      }),
      cycleOverrideColumn({
        field: 'maxOverride',
        headerName: 'Max override',
        description:
          "Run at least this often. A SKU group's can't be faster than its batch's min cycle.",
        bound: 'max',
        minInput: 'minCycle',
        maxInput: 'maxCycle',
        problems,
      }),
      { field: 'minCycle', headerName: 'Min cycle', width: 96, type: 'number' },
      { field: 'maxCycle', headerName: 'Max cycle', width: 96, type: 'number' },
      setByColumn(),
      {
        field: 'replenishmentTypeCode',
        headerName: 'Type',
        width: 80,
        type: 'string',
      },
      {
        field: 'isIncluded',
        headerName: 'Included',
        width: 88,
        type: 'boolean',
      },
      showProblems(
        {
          field: 'runRate',
          headerName: 'Run rate',
          width: 104,
          type: 'number',
          editable: true,
          valueFormatter: number(2),
        },
        problems
      ),
    ],
    [problems]
  )

  const isCellEditable = useCallback(
    (params: GridCellParams) =>
      !(isBatch(params.row) && SKU_ONLY.has(params.field)),
    []
  )

  const getRowClassName = useCallback(
    (params: GridRowClassNameParams) =>
      isBatch(params.row) ? 'row--parent' : '',
    []
  )

  const setExpanded = (expanded: boolean) => {
    const api = cycleGrid.grid.current?.current
    if (!api) return
    data
      .filter(isBatch)
      .forEach((row) => api.setRowChildrenExpansion(row.id, expanded))
  }

  const updateTableData = (changedRows: Record<string, TotalRow>) => {
    const before = new Map(data.map((row) => [row.id, row]))
    const changed = Object.values(changedRows)
    const edits = totalEdits({
      current: changed,
      before: changed.map((row) => before.get(row.id) ?? row),
    })
    if (!edits.products?.length && !edits.batches?.length) {
      toast('No input changes to save')
      return
    }
    preview.holdForSave()
    save(edits)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        overflow: 'auto',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 2,
          pb: 2,
          flexWrap: 'wrap',
        }}
      >
        {cycleGrid.quickFilters}
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          variant="text"
          startIcon={<UnfoldMoreOutlined />}
          onClick={() => setExpanded(true)}
        >
          Expand all
        </Button>
        <Button
          size="small"
          variant="text"
          startIcon={<UnfoldLessOutlined />}
          onClick={() => setExpanded(false)}
        >
          Collapse all
        </Button>
      </Box>
      <LinePreviewNote
        summary={preview.summary}
        isPreviewing={preview.isPreviewing}
        problems={preview.problems}
      />
      <BaseTable
        data={data}
        columns={columns}
        isLoading={isLoading}
        updateTableData={
          updateTableData as unknown as (
            changed: Record<number, TotalRow>
          ) => void
        }
        onPendingChange={preview.onPendingChange}
        getCellClassName={preview.getCellClassName}
        apiRefCallback={cycleGrid.apiRefCallback}
        onCellKeyDown={cycleGrid.onCellKeyDown}
        onFilterModelChange={cycleGrid.onFilterModelChange}
        saveBlockedReason={preview.saveBlockedReason}
        isCellEditable={isCellEditable}
        getRowClassName={getRowClassName}
        treeData
        getTreeDataPath={(row) => row.path}
        groupingColDef={GROUPING_COL_DEF}
        defaultGroupingExpansionDepth={-1}
        cellSelection
        columnGroupingModel={COLUMN_GROUPS}
        initialState={INITIAL_STATE}
        isSaving={isSaving}
        checkBoxSelection={true}
        pinnedColumns={[GRID_TREE_DATA_GROUPING_FIELD]}
        downloadFileName="total-production"
      />
    </Box>
  )
}
